<template>
  <div v-if="!marketplaceStore.product" class="py-3 flex justify-center">
    <BaseSpinner />
  </div>
  <div
    v-else
    class="rounded-xl bg-no-repeat bg-cover bg-center overflow-hidden min-h-[250px] flex flex-col"
    :style="{
    backgroundImage: `url(${(marketplaceStore.product.preview as any).original_url})`,
  }"
  >
    <div
      class="text-white w-full h-full bg-black/60 p-6 md:p-8 flex flex-col grow"
    >
      <div class="flex flex-col">
        <div
          v-if="marketplaceStore.product.entity?.entity_id"
          class="flex items-center gap-2 mb-3"
        >
          <img
            v-if="marketplaceStore.product.entity?.entity_media"
            class="w-5 h-5 rounded-[4px] bg-white"
            :src="marketplaceStore.product.entity?.entity_media"
            :alt="marketplaceStore.product.entity.entity_name || ''"
          />
          <span class="text-sm">{{
            marketplaceStore.product.entity.entity_name
          }}</span>
        </div>
        <div class="text-3xl uppercase font-bold">
          {{ title }}
        </div>
        <div v-if="categoriesNames.length" class="flex gap-2 mt-3">
          <template v-for="category in categoriesNames" :key="category">
            <div
              class="inline-flex rounded-full px-3 py-1 bg-white text-black text-xs"
            >
              {{ category }}
            </div>
          </template>
        </div>
        <div class="mt-6 max-w-[80ch]">
          {{ description }}
        </div>
      </div>
      <div v-if="marketplaceStore.product.prices?.length" class="mt-4 text-xl">
        <template
          v-for="(price, id) in marketplaceStore.product.prices"
          :key="price.id"
        >
          <template v-if="price.type === MarketplaceProductPriceType.Original">
            <span v-if="id > 0" class="mx-3">/</span>
            <span class="whitespace-nowrap"
              >{{ price.value }}&nbsp;{{ price.currency }}</span
            >
            <span
              v-if="getDiscountPrice(price.currency)"
              class="line-through text-white/70 ml-2 whitespace-nowrap"
            >
              {{ getDiscountPrice(price.currency) }}&nbsp;{{ price.currency }}
            </span>
          </template>
        </template>
      </div>
      <div v-if="marketplaceStore.product.properties?.length" class="mt-4">
        <div
          v-for="property in marketplaceStore.product.properties"
          :key="property.property_id"
        >
          <div>
            <span class="font-bold">{{ property.property_name }}:</span>
            {{ property.value }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { MarketplaceProductPriceType } from '~/models/marketplace'
import { useMarketplace } from '~/stores/marketplace'

const i18n = useI18n()
const marketplaceStore = useMarketplace()

const title = computed(() =>
  marketplaceStore.product
    ? getFieldLocale(
        parseFieldLocales(marketplaceStore.product?.title) || '',
        i18n.locale.value
      )
    : ''
)

const description = computed(() =>
  marketplaceStore.product?.description
    ? getFieldLocale(
        parseFieldLocales(marketplaceStore.product.description) || '',
        i18n.locale.value
      )
    : ''
)

const categoriesNames = computed(() =>
  marketplaceStore.product?.categories
    ? marketplaceStore.product.categories.map((item) =>
        getFieldLocale(
          parseFieldLocales(item.category_name) || '',
          i18n.locale.value
        )
      )
    : []
)

function getDiscountPrice(currency: string) {
  return marketplaceStore.product?.prices?.find(
    (price) =>
      price.type === MarketplaceProductPriceType.Discount &&
      price.currency === currency
  )?.value
}
</script>
